import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import _ from 'lodash';
import { Subject, takeUntil } from 'rxjs';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { DetailPanelView } from 'src/app/shared/common/enums/list-panel/panel-view-mode.enum';
import { TCFile } from 'src/app/shared/common/files/TCFile';
import { PanelMenuService } from 'src/app/shared/common/services/panel-menu/panel-menu.service';
import {
    mapToSurveyJobAttachment,
    SurveyJob,
    SurveyJobAttachment,
    SurveyJobPatchData,
    SurveyJobPatchDataKey,
    SurveyJobStatus
} from 'src/app/shared/survey-data-services/survey-job';
import { SurveyJobService } from 'src/app/shared/survey-data-services/survey-job.services';
import { User } from 'src/app/shared/user/user';

@Component({
    selector: 'edit-job-panel',
    templateUrl: './edit-job-panel.component.html'
})
export class EditJobPanelComponent implements OnInit, OnDestroy {
    @Input() job: SurveyJob;
    @Input() linkedFiles: SurveyJobAttachment[];

    @Output() jobUpdated = new EventEmitter<SurveyJob>();
    @Output() isJobUpdating = new EventEmitter<{ isUpdating: boolean; isJobRefreshNeeded: boolean }>();

    private destroyed = new Subject<void>();

    clonedJob: SurveyJob;
    patchData: SurveyJobPatchData = {};
    loading = false;
    ButtonType = ButtonType;
    showAttachFilesPanel = false;
    editLinkedFiles: SurveyJobAttachment[] = [];
    isJobRefreshNeeded = false;

    constructor(
        private surveyJobService: SurveyJobService,
        private panelMenuService: PanelMenuService,
        private messaging: MessagingService,
        private translate: TranslationService
    ) {}

    ngOnInit(): void {
        this.clonedJob = _.cloneDeep(this.job);
        this.editLinkedFiles = _.cloneDeep(this.linkedFiles); // make a copy to edit
        this.panelMenuService.detailsPanelViewStream.pipe(takeUntil(this.destroyed)).subscribe(view => {
            this.showAttachFilesPanel = view === DetailPanelView.AttachFiles;
        });
    }

    ngOnDestroy(): void {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onAssignedUserChange(selectedUsers: Array<User>): void {
        this.clonedJob.assignees = selectedUsers;
        if (selectedUsers.length > 0) {
            this.patchData[SurveyJobPatchDataKey.Assignee] = { id: selectedUsers[0].id };
        } else {
            this.patchData[SurveyJobPatchDataKey.Assignee] = {};
        }
    }

    onJobStatusChange(newStatus: SurveyJobStatus): void {
        this.clonedJob.status = newStatus;
        this.patchData[SurveyJobPatchDataKey.Status] = newStatus;
    }

    onJobTagsChange(updatedTags: string[]): void {
        this.clonedJob.tags = updatedTags;
        this.patchData[SurveyJobPatchDataKey.Tags] = updatedTags;
    }

    //Event from the Attach Files Component
    onFilesSelected(linkedFiles: TCFile[]): void {
        this.editLinkedFiles = mapToSurveyJobAttachment(linkedFiles);
        this.patchData[SurveyJobPatchDataKey.LinkedFiles] = linkedFiles.map(file => file.id);
    }

    //Event from the Chips
    onLinkedFilesChange(updatedFiles: SurveyJobAttachment[]): void {
        this.editLinkedFiles = _.cloneDeep(updatedFiles);
        this.patchData[SurveyJobPatchDataKey.LinkedFiles] = updatedFiles.map(file =>
            file.id ? file.id.split(':').pop() : ''
        );
    }

    updateJob(): void {
        this.loading = true;
        this.isJobUpdating.emit({ isUpdating: true, isJobRefreshNeeded: false });
        this.showAttachFilesPanel = false;
        this.surveyJobService
            .patchSurveyJob(this.job.id, this.job.eTag, this.patchData)
            .then(updatedJob => {
                this.job = updatedJob;
                this.patchData = {};
                this.isJobRefreshNeeded = false;
                this.jobUpdated.emit(updatedJob);
            })
            .catch(error => {
                console.error('Error updating job:', error);
                if (error.status === 412) {
                    this.messaging.showWarning(this.translate.instant('Survey.SurveyEditPanel.ErrorPatchEtagMessage'));
                    this.isJobRefreshNeeded = true;
                } else {
                    this.messaging.showError(this.translate.instant('Survey.SurveyEditPanel.ErrorPatchMessage'));
                    this.isJobRefreshNeeded = false;
                }
            })
            .finally(() => {
                this.loading = false;
                this.isJobUpdating.emit({ isUpdating: false, isJobRefreshNeeded: this.isJobRefreshNeeded });
            });
    }

    discardChanges(): Promise<void> {
        return new Promise(resolve => {
            this.panelMenuService.detailsPanelViewStream.next(DetailPanelView.Detail);
            resolve();
        });
    }

    cancelEditAction(): void {
        if (this.isJobUpdated()) {
            this.panelMenuService.confirmationPopupDataStream.next({
                title: this.translate.instant('TC.MetadataEditor.DiscardChangesWithQuestion'),
                message: this.translate.instant('Survey.DiscardChangesQuestion'),
                confirmButtonText: this.translate.instant('discard'),
                isPermanentAction: false,
                id: 'discard-edit-job-changes',
                confirmAction: () => this.discardChanges()
            });
            return;
        } else {
            this.discardChanges();
        }
    }

    isJobUpdated(): boolean {
        const patch = this.patchData;
        const job = this.job;
        if (!patch || !job) return false;
        for (const key in patch) {
            const patchValue = patch[key as keyof SurveyJobPatchData];
            switch (key) {
                case SurveyJobPatchDataKey.Assignee:
                    // patchValue: {id: string}, job.assignees: User[]
                    const patchAssigneeId = patchValue && (patchValue as any).id;
                    const jobAssignees = Array.isArray(job.assignees) ? job.assignees : [];
                    // If there are multiple assignees, consider as updated
                    if (jobAssignees.length > 1) return true;
                    const jobAssigneeId = jobAssignees.length > 0 ? jobAssignees[0].id : undefined;
                    if (patchAssigneeId !== jobAssigneeId) return true;
                    break;
                case SurveyJobPatchDataKey.Tags:
                    // Compare arrays as sets: order does not matter, but values must match
                    const patchTags = Array.isArray(patchValue) ? patchValue : [];
                    const jobTags = Array.isArray(job.tags) ? job.tags : [];
                    if (!_.isEqual(_.sortBy(patchTags), _.sortBy(jobTags))) {
                        return true;
                    }
                    break;
                case SurveyJobPatchDataKey.Status:
                    if (patchValue !== job.status) return true;
                    break;
                case SurveyJobPatchDataKey.LinkedFiles:
                    // patchValue: string[] (ids), job.linkedFiles: SurveyJobAttachment[] or Partial[]
                    const jobLinkedFileIds = Array.isArray(job.linkedFiles)
                        ? job.linkedFiles.map(f => (f as any).id)
                        : [];
                    if (!_.isEqual(patchValue, jobLinkedFileIds)) return true;
                    break;
            }
        }
        return false;
    }
}
